class EventClass {
  _eventCallbacks = {};

  constructor() {
    this.addEventListener = this.addEventListener.bind(this);
    this.removeEventListener = this.removeEventListener.bind(this);
    this.dispatchEvent = this.dispatchEvent.bind(this);
    this.unload = this.unload.bind(this);
  }

  /**
   * Add an event callback similar to the way you'd do it for DOM elements
   * @param {string} eventType - the type of event to attach the callback to
   * @param {function} callbackRef - a callback function to call when this event is triggered
   */
  addEventListener(eventType, callbackRef) {
    // Make sure an event array exists before trying to add callback to it
    if ((typeof eventType !== 'string') && (typeof callbackRef !== 'function')) return false;

    if (!this._eventCallbacks[eventType] || !Array.isArray(this._eventCallbacks[eventType])) {
      this._eventCallbacks[eventType] = [];
    }

    this._eventCallbacks[eventType].push(callbackRef);
    return true;
  }

  /**
   * This function removes an attached callback function from the list of callbacks.
   * It removes the last in the in, if for some reason there is more than one reference
   * to the same function attached
   * @param  {string} eventType - type of event that the callback is attached to
   * @param  {function} callbackRef - the callback to be removed from the callback array
   * @return {[type]}
   */
  removeEventListener(eventType, callbackRef) {
    if (
      !this._eventCallbacks[eventType] ||
      !Array.isArray(this._eventCallbacks[eventType])
    ) return false;

    for (let i = this._eventCallbacks[eventType].length - 1; i >= 0; i -= 1) {
      if (this._eventCallbacks[eventType][i] === callbackRef) {
        // Create new array without the matching callback
        this._eventCallbacks[eventType] = [
          ...this._eventCallbacks[eventType].slice(0, i),
          ...this._eventCallbacks[eventType].slice(i + 1),
        ];
        return true;
      }
    }
    return false;
  }

  /**
   * Dispatches an event to registered callbacks
   */
  dispatchEvent(eventType, eventData) {
    if (!this._eventCallbacks[eventType] || !Array.isArray(this._eventCallbacks[eventType])) {
      return {
        defaultPrevented: false,
      };
    }

    // Events and settings that are set by the listener callbacks
    let shouldPreventDefault = false;
    let shouldHaltListenerChain = false;
    const parameters = {};
    const asyncEvents = [];

    // Functions exposed to the listener callback
    const listenerActions = {
      preventDefault: () => { shouldPreventDefault = true; },
      haltListenerChain: () => { shouldHaltListenerChain = true; },
      setParameter: (key, value) => { parameters[key] = value; },
      addAsyncEvent: (promise) => { asyncEvents.push(promise); },
    };

    // The event data that is passed to the callback function
    const callbackEventData = {
      ...eventData,
      ...listenerActions,
    };

    const callbacks = [...this._eventCallbacks[eventType]];

    // Loop over each callback and break out of loop if listener chain gets halted
    for (let i = 0; i < callbacks.length; i += 1) {
      const callback = callbacks[i];
      callback(callbackEventData);
      if (shouldHaltListenerChain) break;
    }

    return {
      defaultPrevented: shouldPreventDefault,
      listenerChainHalted: shouldHaltListenerChain,
      asyncEvents: Promise.all(asyncEvents),
      parameters,
    };
  }

  // Releases any variable references so that it can be cleaned up gracefully when no longer needed
  unload() {
    this._eventCallbacks = {};
  }
}

export default EventClass;
